import type { VsmContext, PageConfigEntry, PageConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { MenuContainerPageConfig, MenuContainerData, MenuToggleData, AnimationData } from '../types'
import type { AnimationsBehavior } from '@wix/thunderbolt-becky-types'
import { name } from '../symbols'

const AnimationsBehaviorKeys: Array<keyof AnimationsBehavior> = ['name', 'delay', 'duration', 'targetId', 'params']

function pickBehaviorKeys(item: AnimationsBehavior): AnimationData {
	const result: Record<string, unknown> = {}
	for (const key of AnimationsBehaviorKeys) {
		if (item[key] !== undefined) result[key] = item[key]
	}
	return result as AnimationData
}

const menuContainerPageConfig: PageConfigFn<MenuContainerPageConfig> = ({ api }: VsmContext) => {
	const viewMode = api.env.getViewMode()

	const toggleBehaviors = api.components.getBehaviorsByName('toggle')
	const openBehaviors = api.components.getBehaviorsByName('open')
	const closeBehaviors = api.components.getBehaviorsByName('close')

	const allBehaviors = [...toggleBehaviors, ...openBehaviors, ...closeBehaviors]
	const menuToggleToMenuContainerId: Record<string, string> = {}
	for (const { action, behavior } of allBehaviors) {
		menuToggleToMenuContainerId[action.sourceId] = behavior.targetId
	}

	const menuContainers = api.components.getComponentsByType('MenuContainer')
	const menuContainerConfig: Record<string, MenuContainerData> = {}
	for (const menuContainerId of Object.keys(menuContainers)) {
		const filtered = api.components.getAnimationsByViewMode(menuContainerId, viewMode!)

		const byAction: Record<string, AnimationsBehavior> = {}
		for (const item of filtered) {
			if (item.action) byAction[item.action] = item
		}

		const animations: Partial<MenuContainerData['animations']> = {}
		if (byAction.inlinePopupIn) animations.inBehavior = pickBehaviorKeys(byAction.inlinePopupIn)
		if (byAction.inlinePopupOut) animations.outBehavior = pickBehaviorKeys(byAction.inlinePopupOut)

		menuContainerConfig[menuContainerId] = { animations: animations as MenuContainerData['animations'] }
	}

	const menuToggles = api.components.getComponentsByType('MenuToggle')
	const vectorImages = api.components.getComponentsByType('VectorImage')
	const toggleCandidates = { ...menuToggles, ...vectorImages }

	const menuTogglesConfig: Record<string, MenuToggleData> = {}
	for (const compId of Object.keys(toggleCandidates)) {
		const menuContainerId = menuToggleToMenuContainerId[compId]
		if (menuContainerId) {
			menuTogglesConfig[compId] = { menuContainerId }
		}
	}

	const hasContent = Object.keys(menuContainerConfig).length > 0 || Object.keys(menuTogglesConfig).length > 0

	if (!hasContent) {
		return { shouldLoadPageFeature: false }
	}

	return {
		shouldLoadPageFeature: true,
		pageConfig: { menuContainerConfig, menuTogglesConfig },
	}
}

export const pageConfig: PageConfigEntry<MenuContainerPageConfig> = {
	name,
	configFactory: menuContainerPageConfig,
	experiment: 'specs.thunderbolt.vsmPageConfigs',
}
